import { PkCountdown, PkCountdownTimer, PkDivider } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useMemo } from 'react';

import { useWindowSize } from '@hooks/useWindowSize';
import SingleSponsor from '@shared/RandomSponsors/SingleSponsor';
import { useSponsor } from '@shared/RandomSponsors/useSponsor';
import { useTranslateFn } from '@shared/Translate';
import { language } from '@utilities/browser';

import * as styles from './styles.module.scss';

export const Countdown = ({
  countdownDate,
  sponsorTag,
  customSponsorImage,
  countdownLabel,
}: {
  countdownDate: string;
  sponsorTag?: string;
  customSponsorImage?: boolean;
  countdownLabel?: string;
}) => {
  const { sponsor } = useSponsor(sponsorTag);

  const showSponsor = useMemo(() => sponsor && sponsorTag, [sponsor, sponsorTag]);

  const introTextLabel = useMemo(() => sponsor?.introText?.translations?.[language.toUpperCase()], [sponsor]);
  const { isExtraSmallMobile, isMobile } = useWindowSize();
  const t = useTranslateFn();

  const format = useMemo(() => {
    const matchDate = Date.parse(countdownDate);
    const difference = Math.abs(matchDate - Date.now());
    const isLastDay = difference <= 24 * 60 * 60;
    const extraMobileFormat = isLastDay ? 'HH:mm:ss' : 'DD:HH:mm';

    return isExtraSmallMobile ? extraMobileFormat : 'DD:HH:mm:ss';
  }, [countdownDate, isExtraSmallMobile]);

  return (
    countdownDate && (
      <div
        className={clsx(styles.wrapper, {
          [styles['notSponsored']]: !showSponsor,
        })}
      >
        {!!showSponsor && !!introTextLabel && !customSponsorImage && (
          <div className={styles.introText}> {introTextLabel}</div>
        )}
        <div className={styles.countdown}>
          {!!showSponsor && <SingleSponsor sponsor={sponsor} creative='exclusive-section' position='header' />}
          <PkCountdown>
            {customSponsorImage && !isMobile && (
              <div className={styles.countdownTitle} slot='heading'>
                {t(countdownLabel ?? 'countdown_to_final')}
              </div>
            )}
            <PkCountdownTimer
              deadline={countdownDate}
              format={format}
              daysLabel={t('days')}
              hoursLabel={t(isMobile ? 'hours_abbr_countdown' : 'hours')}
              minutesLabel={t(isMobile ? 'stats_minutes_abbr' : 'minutes')}
              secondsLabel={t(isMobile ? 'seconds_abbr_countdown' : 'seconds')}
            >
              {customSponsorImage && (
                <>
                  <PkDivider direction='vertical' slot='days-separator' />
                  <span slot='hours-separator'>:</span>
                  <span slot='minutes-separator'>:</span>
                </>
              )}
            </PkCountdownTimer>
          </PkCountdown>
        </div>
      </div>
    )
  );
};

export default memo(Countdown);
